'use client';

import { useMemo, useRef, useState } from 'react';
import { consumeToolUsage } from '@/lib/browser-tool-usage';

const DOOR_AREA_SQFT = 21; // a standard interior door opening, ~3ft x 7ft
const WINDOW_AREA_SQFT = 15; // a typical window opening

export default function BrowserPaintCalculator() {
  const fired = useRef(false);
  const fireUsage = () => {
    if (fired.current) return;
    fired.current = true;
    void consumeToolUsage('paint-calculator', '').catch(() => undefined);
  };

  const [lengthFt, setLengthFt] = useState(12);
  const [widthFt, setWidthFt] = useState(10);
  const [heightFt, setHeightFt] = useState(8);
  const [doors, setDoors] = useState(1);
  const [windows, setWindows] = useState(2);
  const [coats, setCoats] = useState(2);
  const [coveragePerGallon, setCoveragePerGallon] = useState(350);
  const [includeCeiling, setIncludeCeiling] = useState(false);

  function onChange<T>(setter: (v: T) => void) {
    return (v: T) => {
      fireUsage();
      setter(v);
    };
  }

  const result = useMemo(() => {
    const perimeter = 2 * (lengthFt + widthFt);
    const wallArea = perimeter * heightFt;
    const openings = doors * DOOR_AREA_SQFT + windows * WINDOW_AREA_SQFT;
    const ceilingArea = includeCeiling ? lengthFt * widthFt : 0;
    const paintableArea = Math.max(0, wallArea - openings) + ceilingArea;
    const totalArea = paintableArea * coats;
    const gallons = coveragePerGallon > 0 ? totalArea / coveragePerGallon : 0;
    const gallonsRounded = Math.ceil(gallons * 4) / 4; // round up to the nearest quart
    const liters = gallonsRounded * 3.78541;
    return { paintableArea, gallonsRounded, liters };
  }, [lengthFt, widthFt, heightFt, doors, windows, coats, coveragePerGallon, includeCeiling]);

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Paint</span>
        <h2 className="tool-live-title">Paint calculator</h2>
        <p className="tool-live-subtitle">
          Enter a room&apos;s dimensions to get the wall area and how many gallons of paint you
          need.
        </p>
      </div>

      <div className="tool-live-grid">
        <section
          className="tool-live-uploader"
          style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))' }}
        >
          <label className="tool-live-field">
            <span>Room length (ft)</span>
            <input
              min={0}
              onChange={(e) => onChange(setLengthFt)(Number(e.target.value) || 0)}
              step={0.5}
              type="number"
              value={lengthFt}
            />
          </label>
          <label className="tool-live-field">
            <span>Room width (ft)</span>
            <input
              min={0}
              onChange={(e) => onChange(setWidthFt)(Number(e.target.value) || 0)}
              step={0.5}
              type="number"
              value={widthFt}
            />
          </label>
          <label className="tool-live-field">
            <span>Wall height (ft)</span>
            <input
              min={0}
              onChange={(e) => onChange(setHeightFt)(Number(e.target.value) || 0)}
              step={0.5}
              type="number"
              value={heightFt}
            />
          </label>
          <label className="tool-live-field">
            <span>Doors</span>
            <input
              min={0}
              onChange={(e) => onChange(setDoors)(Number(e.target.value) || 0)}
              type="number"
              value={doors}
            />
          </label>
          <label className="tool-live-field">
            <span>Windows</span>
            <input
              min={0}
              onChange={(e) => onChange(setWindows)(Number(e.target.value) || 0)}
              type="number"
              value={windows}
            />
          </label>
          <label className="tool-live-field">
            <span>Coats</span>
            <input
              min={1}
              onChange={(e) => onChange(setCoats)(Number(e.target.value) || 1)}
              type="number"
              value={coats}
            />
          </label>
          <label className="tool-live-field">
            <span>Coverage per gallon (sq ft)</span>
            <input
              min={1}
              onChange={(e) => onChange(setCoveragePerGallon)(Number(e.target.value) || 1)}
              type="number"
              value={coveragePerGallon}
            />
          </label>
          <label className="tool-live-checkbox">
            <input
              checked={includeCeiling}
              onChange={(e) => onChange(setIncludeCeiling)(e.target.checked)}
              type="checkbox"
            />
            <span>Include the ceiling</span>
          </label>
        </section>

        <section className="tool-live-preview">
          <p className="tool-live-panel-note">
            <strong>{result.paintableArea.toFixed(0)} sq ft</strong>
            <span>paintable area, one coat</span>
          </p>
          <p className="tool-live-panel-note">
            <strong>{result.gallonsRounded.toFixed(2)} gallons</strong>
            <span>
              {result.liters.toFixed(1)} liters, for {coats} coat(s)
            </span>
          </p>
          <p className="tool-live-panel-note">
            Coverage varies by paint and surface (350 sq ft/gallon is a common average for smooth
            walls); check your can&apos;s label for the exact figure.
          </p>
        </section>
      </div>
    </div>
  );
}
